/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo } from "react";
import { SelectProps } from "antd";
import { Select } from "antd";
import OnahInputError from "components/OnahInputError";
import classNames from "classnames";
import OnahLabel from "components/OnahLabel";
import { useOnahDispatch, useOnahSelector } from "store/hooks";
import { getBanks } from "store/slices/common";
import { currencies } from "utils/currency";
import { ReactComponent as Lock } from "assets/svgs/Lock.svg";

type Props = {
  wrapperClassName?: string;
  className?: string;
  error?: string;
  label?: string;
  prefixIcon?: React.ReactNode;
  readOnly?: boolean;
} & SelectProps;

const OnahSelect: React.FC<Props> = ({
  className,
  wrapperClassName,
  error,
  label,
  prefixIcon,
  value,
  onBlur,
  readOnly,
  ...props
}) => {
  return (
    <div className={classNames(wrapperClassName, "relative")}>
      <OnahLabel label={label} />
      {prefixIcon && (
        <span
          style={{
            position: "absolute",
            top: "14px",
            left: "2px",
            zIndex: 3,
            paddingRight: "10px",
          }}
        >
          {prefixIcon}
        </span>
      )}
      <Select
        {...props}
        value={value || undefined}
        placeholder={props.placeholder || label}
        onBlur={onBlur}
        disabled={readOnly}
        suffixIcon={readOnly ? <Lock /> : props.suffixIcon}
        getPopupContainer={(trigger) => trigger.parentElement}
        className={classNames(
          "w-full text-lg [&>.ant-select-selection-item]:!text-lg [&>.ant-select-selector]:!border-0",
          "[&>.ant-select-selection-placeholder]:!text-gray-placeholder [&>.ant-select-selection-item]:!text-black",
          "[&>.ant-select-selection-item]:!font-semibold [&>.ant-select-selector]:!bg-gray-restingField",
          error
            ? "[&>.ant-select-selector]:!border [&>.ant-select-selector]:!border-error-700 [&>.ant-select-selector]:focus:!border-error-700"
            : "[&>.ant-select-selector]:hover:!border [&>.ant-select-selector]:hover:!border-primary-ctaStroke",
          readOnly ?
            classNames(
              "[&>.ant-select-selector]:!bg-white [&>.ant-select-selector]:!border [&>.ant-select-selector]:!border-gray-inputFieldStroke",
              "overflow-hidden rounded-[6px] [&>.ant-select-selector]:hover:!cursor-text [&_.ant-select-selection-search-input]:hover:!cursor-text" ): "",
          className
        )}
      />
      <OnahInputError error={error} showError={!!error} />
    </div>
  );
};

export const OnahBankSelect: React.FC<Omit<Props, 'options'>> = ({placeholder, ...props}) => {
  const {banks} = useOnahSelector(state => state.common);
  const dispatch = useOnahDispatch();

  useEffect(() => {
    dispatch(getBanks())
  }, [])

  const mappedBanks = useMemo(() => {
    return banks.map(bank => ({ label: bank.name, value: bank.code.toString() }))
  }, [banks])

  return (
    <OnahSelect
      {...props}
      placeholder={placeholder ?? "Select a Bank"}
      options={mappedBanks}
    />
  );
}

export const OnahCurrency: React.FC<Omit<Props & {shortLabel?: boolean}, 'options'>> = ({placeholder, shortLabel = false, ...props}) => {

  const mappedCurrency = useMemo(() => {
    return Object.keys(currencies).map(currency => ({
      label: shortLabel ? currency : currencies[currency as keyof typeof currencies].currency,
      value: currency
    }))
  }, [])

  return (
    <OnahSelect
      {...props}
      placeholder={placeholder ?? "Currency"}
      options={mappedCurrency}
    />
  );
}

export const OnahSelectCountries: React.FC<Omit<Props, 'options'>> = ({placeholder, ...props}) => {

  return (
    <OnahSelect
      {...props}
      placeholder={placeholder ?? "Select a Country"}
      options={[{ label: "Nigeria", value: "NG" }]}
    />
  );
}

export default OnahSelect;
